import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { getApiBaseUrl, getAuthHeaders } from "../../utils/apiHelpers";
import { logout } from "../../utils/authUtils";
import SidebarLayout from "../shops/SidebarLayout";
import FreelancerSidebarLayout from "../freelancer/FreelancerSidebarLayout";

/**
 * REFERRAL AGENT DASHBOARD (2026-10-29).
 *
 * The page that has to MOTIVATE — the user's words were *"so that can actually motivate"*. So it leads with
 * the code and the shareable link, then what has been earned, then the businesses referred.
 *
 * Decisions reflected here:
 *   - The agent sees BUSINESS NAMES (agreed 2026-10-28).
 *   - Money arrives from the API in CENTS and is formatted here: the server never sends a display string, so
 *     there is one rounding rule.
 *   - An R1/R4 who has not opted in sees the rate and an opt-in button — a partner has to CHOOSE to refer.
 *   - A 404 means the feature is switched off. That is not an error: the flag ships off by default, so the
 *     page says "not available yet" calmly rather than showing a failure.
 */

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

const statusTone = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "active":
      return { background: "#ecfdf5", color: "#065f46", label: "Active" };
    case "trial":
      return { background: "#eff6ff", color: "#1d4ed8", label: "On trial" };
    case "past_due":
      return { background: "#fffbeb", color: "#92400e", label: "Payment due" };
    case "canceled":
    case "unpaid":
      return { background: "#fef2f2", color: "#991b1b", label: "Lapsed" };
    default:
      return { background: "#f1f5f9", color: "#334155", label: "—" };
  }
};

const styles = {
  agentBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "10px 16px",
    background: "#0f172a",
    color: "#ffffff",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  agentBarBrand: { fontSize: "13px", fontWeight: 600, letterSpacing: "0.02em" },
  agentBarOut: {
    border: "1px solid rgba(255,255,255,0.35)",
    background: "transparent",
    color: "#ffffff",
    borderRadius: "8px",
    padding: "5px 10px",
    fontSize: "12px",
    cursor: "pointer",
  },
  page: {
    maxWidth: "960px",
    margin: "0 auto",
    padding: "24px 16px 48px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: "#0f172a",
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "16px",
  },
  label: {
    fontSize: "11px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#64748b",
    fontWeight: 600,
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "12px",
    marginBottom: "16px",
  },
  stat: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "14px 16px",
  },
  codeBox: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "10px",
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: "10px",
    padding: "12px 14px",
    marginBottom: "10px",
  },
  code: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    fontWeight: 700,
    fontSize: "16px",
    letterSpacing: "0.08em",
    wordBreak: "break-all",
  },
  btn: {
    border: "1px solid #1d4ed8",
    background: "#1d4ed8",
    color: "#ffffff",
    borderRadius: "8px",
    padding: "8px 14px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  btnGhost: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    borderRadius: "8px",
    padding: "8px 14px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "12px 0",
    borderBottom: "1px solid #f1f5f9",
  },
  muted: { color: "#64748b", fontSize: "13px" },
  err: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    borderRadius: "10px",
    padding: "12px 14px",
    marginBottom: "16px",
    fontSize: "14px",
  },
};

function ReferralAgentBody() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [data, setData] = useState(null);
  const [optingIn, setOptingIn] = useState(false);
  const [copied, setCopied] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setUnavailable(false);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(
        `${getApiBaseUrl()}/api/platform-plans/referral/me`,
        { headers },
      );
      setData(res.data);
    } catch (err) {
      // The feature ships disabled by default, so a 404 is expected, not broken.
      if (err?.response?.status === 404) {
        setUnavailable(true);
      } else {
        setError(
          err?.response?.data?.message ||
            "Unable to load your referrals right now.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const optIn = async () => {
    setOptingIn(true);
    setError("");
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(
        `${getApiBaseUrl()}/api/platform-plans/referral/opt-in`,
        {},
        { headers },
      );
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to opt in to referrals.");
    } finally {
      setOptingIn(false);
    }
  };

  const copy = async (value, kind) => {
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Older / insecure contexts: a temporary input is the reliable fallback.
        const el = document.createElement("textarea");
        el.value = value;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(kind);
      window.setTimeout(() => setCopied(""), 2000);
    } catch (_) {
      setError("Could not copy — select the text and copy it manually.");
    }
  };

  const referrals = useMemo(
    () => (Array.isArray(data?.referrals) ? data.referrals : []),
    [data],
  );

  if (loading) {
    return (
      <div style={styles.page}>
        <p style={styles.muted}>Loading your referrals…</p>
      </div>
    );
  }

  if (unavailable) {
    return (
      <div style={styles.page}>
        <h1 style={{ fontSize: "22px", margin: "0 0 8px" }}>Referrals</h1>
        <div style={styles.card}>
          <p style={{ marginTop: 0 }}>Referrals aren’t available yet.</p>
          <p style={{ ...styles.muted, marginBottom: 0 }}>
            This programme is being prepared. Check back soon.
          </p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={styles.page}>
        <div style={styles.err}>{error}</div>
        <button type="button" style={styles.btn} onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  // Eligible but not opted in: an R1 or R4 has to choose to refer.
  if (data && !data.isAgent) {
    return (
      <div style={styles.page}>
        <h1 style={{ fontSize: "22px", margin: "0 0 8px" }}>Refer &amp; earn</h1>
        <div style={styles.card}>
          {data.eligibleToOptIn ? (
            <>
              <p style={{ marginTop: 0 }}>
                Earn{" "}
                <strong>
                  {Number.isFinite(Number(data.commissionPercent))
                    ? `${data.commissionPercent}%`
                    : "commission"}
                </strong>{" "}
                of what OGESTYLE collects from every business you bring in.
              </p>
              <ul style={styles.muted}>
                <li>You get one code and one link to share.</li>
                <li>
                  Commission starts when they first pay — nothing is earned
                  during their free trial.
                </li>
                <li>
                  You’ll see each business you referred and what it has earned.
                </li>
              </ul>
              {error ? <div style={styles.err}>{error}</div> : null}
              <button
                type="button"
                style={styles.btn}
                onClick={optIn}
                disabled={optingIn}
              >
                {optingIn ? "Setting up…" : "Opt in and get my link"}
              </button>
            </>
          ) : (
            <p style={{ marginTop: 0 }}>
              Referrals are available to business owners and freelancers.
            </p>
          )}
        </div>
      </div>
    );
  }

  const totals = data?.totals || {};

  return (
    <div style={styles.page}>
      <h1 style={{ fontSize: "22px", margin: "0 0 4px" }}>Referrals</h1>
      <p style={{ ...styles.muted, marginTop: 0 }}>
        Share your link. Commission is {data?.commissionPercent}% of what
        OGESTYLE collects, net of card fees.
        {data?.commissionCapMonths
          ? ` Each business earns for up to ${data.commissionCapMonths} months.`
          : ""}
      </p>

      {error ? <div style={styles.err}>{error}</div> : null}

      <div style={styles.card}>
        <div style={{ ...styles.label, marginBottom: "10px" }}>
          Your code and link
        </div>
        <div style={styles.codeBox}>
          <span style={styles.code}>{data?.code || "—"}</span>
          <button
            type="button"
            style={styles.btnGhost}
            onClick={() => copy(data?.code, "code")}
          >
            {copied === "code" ? "Copied" : "Copy code"}
          </button>
        </div>
        <div style={styles.codeBox}>
          <span style={{ ...styles.code, fontSize: "13px", fontWeight: 500 }}>
            {data?.link || "—"}
          </span>
          <button
            type="button"
            style={styles.btn}
            onClick={() => copy(data?.link, "link")}
          >
            {copied === "link" ? "Copied" : "Copy link"}
          </button>
        </div>
        <p style={{ ...styles.muted, margin: 0 }}>
          Anyone who signs up with it pays the current offer, and you get the
          credit.
        </p>
      </div>

      <div style={styles.statGrid}>
        <div style={styles.stat}>
          <div style={styles.label}>Referrals</div>
          <div style={{ fontSize: "22px", fontWeight: 700 }}>
            {totals.referrals ?? referrals.length}
          </div>
        </div>
        <div style={styles.stat}>
          <div style={styles.label}>This period</div>
          <div style={{ fontSize: "22px", fontWeight: 700 }}>
            {money(totals.thisPeriodCents)}
          </div>
        </div>
        <div style={styles.stat}>
          <div style={styles.label}>Earned to date</div>
          <div style={{ fontSize: "22px", fontWeight: 700 }}>
            {money(totals.earnedToDateCents)}
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ ...styles.label, marginBottom: "6px" }}>
          Businesses you referred
        </div>

        {referrals.length === 0 ? (
          <p style={{ ...styles.muted, margin: "8px 0 0" }}>
            No referrals yet — share your link to get started.
          </p>
        ) : (
          referrals.map((entry, index) => {
            const tone = statusTone(entry.status);
            return (
              <div
                key={`${entry.businessName || "referral"}-${entry.locationId || index}`}
                style={{
                  ...styles.row,
                  borderBottom:
                    index === referrals.length - 1
                      ? "none"
                      : styles.row.borderBottom,
                }}
              >
                <div style={{ minWidth: "180px", flex: "1 1 auto" }}>
                  <div style={{ fontWeight: 600 }}>
                    {entry.businessName || "Business"}
                  </div>
                  <div style={styles.muted}>
                    {entry.commissionPercent}% · since{" "}
                    {formatDate(entry.attributedAt)}
                    {entry.commissionEndsAt
                      ? ` · until ${formatDate(entry.commissionEndsAt)}`
                      : ""}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span
                    style={{
                      background: tone.background,
                      color: tone.color,
                      borderRadius: "999px",
                      padding: "3px 10px",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {tone.label}
                  </span>
                  <div style={{ fontSize: "13px", marginTop: "6px" }}>
                    {money(entry.monthlyCommissionCents)} / month ·{" "}
                    <strong>{money(entry.earnedToDateCents)}</strong> earned
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function readRoles() {
  try {
    const parsed = JSON.parse(localStorage.getItem("ogestyle_roles") || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

/**
 * An R1 or R4 already lives inside a sidebar, so this page goes inside the one they already use —
 * otherwise it is an orphan that appears from nowhere and strands them.
 *
 * An R9 now has its own multi-page area (2026-11-01), so they are sent there instead: the shell they used
 * to get here was a single 30px bar with one page underneath it, which left them nowhere to look at
 * earnings or register a bank account.
 */
function ReferralAgentShell({ children }) {
  const roles = readRoles();

  if (roles.includes("R9") && !roles.includes("R1") && !roles.includes("R4")) {
    return <Navigate to="/dashboard/agent" replace />;
  }
  if (roles.includes("R1")) return <SidebarLayout>{children}</SidebarLayout>;
  if (roles.includes("R4"))
    return <FreelancerSidebarLayout>{children}</FreelancerSidebarLayout>;

  return (
    <>
      <div style={styles.agentBar}>
        <span style={styles.agentBarBrand}>OGESTYLE · Referral programme</span>
        <button type="button" style={styles.agentBarOut} onClick={() => logout()}>
          Sign out
        </button>
      </div>
      {children}
    </>
  );
}

function ReferralAgentDashboard() {
  return (
    <ReferralAgentShell>
      <ReferralAgentBody />
    </ReferralAgentShell>
  );
}

export default ReferralAgentDashboard;
